import { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { FolderOpen, ArrowLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import type { CategoryBreakdown, SubcategoryBreakdown } from '../../types';

interface CategoryBreakdownChartProps {
  data: CategoryBreakdown[];
  formatCurrency: (amount: number) => string;
  // Paramètres de période pour le drill-down
  month?: number;
  year?: number;
  weekStart?: string;
  weekEnd?: string;
  isPersonal?: boolean;
  noCard?: boolean;
}

interface DrillDownState {
  level: 'categories' | 'subcategories';
  selectedCategory: CategoryBreakdown | null;
  subcategories: SubcategoryBreakdown[];
  loading: boolean;
}

export default function CategoryBreakdownChart({
  data,
  formatCurrency,
  month,
  year,
  weekStart,
  weekEnd,
  isPersonal = false,
  noCard = false,
}: CategoryBreakdownChartProps) {
  const { t } = useTranslation('dashboard');
  const { translateCategoryName } = useCategoryTranslation();
  const [drillDown, setDrillDown] = useState<DrillDownState>({
    level: 'categories',
    selectedCategory: null,
    subcategories: [],
    loading: false,
  });

  // Reset drill-down quand les données changent
  useEffect(() => {
    setDrillDown({
      level: 'categories',
      selectedCategory: null,
      subcategories: [],
      loading: false,
    });
  }, [data]);

  const handleCategoryClick = async (category: CategoryBreakdown) => {
    setDrillDown(prev => ({ ...prev, loading: true }));

    try {
      const endpoint = isPersonal
        ? `/settings/personal-category-breakdown/${category.categoryId}`
        : `/settings/category-breakdown/${category.categoryId}`;

      const params = new URLSearchParams();
      if (weekStart && weekEnd) {
        params.append('weekStart', weekStart);
        params.append('weekEnd', weekEnd);
      } else {
        if (month !== undefined) params.append('month', month.toString());
        if (year !== undefined) params.append('year', year.toString());
      }

      const response = await api.get(`${endpoint}?${params.toString()}`);

      setDrillDown({
        level: 'subcategories',
        selectedCategory: category,
        subcategories: response.data.data,
        loading: false,
      });
    } catch (error) {
      console.error('Error loading subcategories:', error);
      setDrillDown(prev => ({ ...prev, loading: false }));
    }
  };

  const handleBack = () => {
    setDrillDown({
      level: 'categories',
      selectedCategory: null,
      subcategories: [],
      loading: false,
    });
  };

  const CustomTooltip = ({ active, payload }: {
    active?: boolean;
    payload?: Array<{ payload: CategoryBreakdown | SubcategoryBreakdown }>
  }) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const name = 'categoryName' in item
        ? translateCategoryName(item.categoryId, item.categoryName, item.categoryIsDefault)
        : translateCategoryName(item.subcategoryId, item.subcategoryName);
      return (
        <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-100">
          <p className="font-medium text-gray-900">{name}</p>
          <p className="text-sm text-gray-600">{formatCurrency(item.amount)}</p>
          <p className="text-sm text-gray-500">{item.percentage.toFixed(1)}%</p>
        </div>
      );
    }
    return null;
  };

  // Cas sans données
  if (data.length === 0) {
    const emptyContent = (
      <>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('categoryBreakdownDetails.expensesByCategory')}</h3>
        <div className="flex flex-col items-center justify-center h-48 text-gray-500">
          <FolderOpen className="w-12 h-12 mb-2 opacity-50" />
          <p className="text-sm">{t('categoryBreakdownDetails.noExpenses')}</p>
        </div>
      </>
    );
    if (noCard) return emptyContent;
    return <div className="card h-full">{emptyContent}</div>;
  }

  const currentData = drillDown.level === 'categories' ? data : drillDown.subcategories;
  const title = drillDown.level === 'categories'
    ? t('categoryBreakdownDetails.expensesByCategory')
    : drillDown.selectedCategory ? translateCategoryName(drillDown.selectedCategory.categoryId, drillDown.selectedCategory.categoryName, drillDown.selectedCategory.categoryIsDefault) : t('categoryBreakdownDetails.noSubcategories');

  // Préparer les données pour le graphique
  const chartData = currentData.map(item => ({
    ...item,
    name: 'categoryName' in item
      ? translateCategoryName(item.categoryId, item.categoryName, item.categoryIsDefault)
      : translateCategoryName(item.subcategoryId, item.subcategoryName),
  }));

  const mainContent = (
    <>
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        {drillDown.level === 'subcategories' && (
          <button
            onClick={handleBack}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
            title={t('categoryBreakdownDetails.backToCategories')}
          >
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
        )}
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>

      {/* Content */}
      {drillDown.loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : currentData.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <FolderOpen className="w-12 h-12 mb-2 opacity-50" />
          <p className="text-sm">{t('categoryBreakdownDetails.noSubcategories')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Colonne gauche : Roue avec labels */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="45%"
                  innerRadius={35}
                  outerRadius={55}
                  paddingAngle={2}
                  dataKey="amount"
                  nameKey="name"
                  label={({ cx, cy, midAngle, outerRadius, name, percentage, fill }) => {
                    if (percentage <= 5) return null;

                    const RADIAN = Math.PI / 180;
                    const radius = outerRadius + 20;
                    let x = cx + radius * Math.cos(-midAngle * RADIAN);
                    let y = cy + radius * Math.sin(-midAngle * RADIAN);

                    // Tronquer le nom
                    const maxLen = 15;
                    const shortName = name.length > maxLen ? `${name.substring(0, maxLen)}..` : name;
                    const labelText = `${shortName}: ${percentage.toFixed(0)}%`;

                    // Limites du conteneur (basées sur cx qui est le centre)
                    const minX = 10;
                    const maxX = cx * 2 - 10;
                    const minY = 15;
                    const maxY = cy * 2 - 30; // Espace pour la légende

                    // Clamper la position pour rester dans les limites
                    x = Math.max(minX, Math.min(maxX, x));
                    y = Math.max(minY, Math.min(maxY, y));

                    // Déterminer l'alignement selon la position finale
                    let textAnchor: 'start' | 'middle' | 'end';
                    if (x <= minX + 20) {
                      textAnchor = 'start';
                    } else if (x >= maxX - 20) {
                      textAnchor = 'end';
                    } else {
                      textAnchor = 'middle';
                    }

                    return (
                      <text
                        x={x}
                        y={y}
                        fill={fill}
                        stroke="white"
                        strokeWidth={3}
                        paintOrder="stroke"
                        textAnchor={textAnchor}
                        dominantBaseline="central"
                        fontSize={11}
                        fontWeight={500}
                      >
                        {labelText}
                      </text>
                    );
                  }}
                  labelLine={false}
                  onClick={(_, index) => {
                    if (drillDown.level === 'categories') {
                      handleCategoryClick(data[index]);
                    }
                  }}
                  style={{ cursor: drillDown.level === 'categories' ? 'pointer' : 'default' }}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  formatter={(value, entry) => (
                    <span className="text-xs" style={{ color: entry.color }}>{value}</span>
                  )}
                  wrapperStyle={{ fontSize: '10px' }}
                  layout="horizontal"
                  align="center"
                  verticalAlign="bottom"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Colonne droite : Liste triée cliquable */}
          <div className="max-h-64 overflow-y-auto">
            <div className="space-y-1">
              {[...currentData]
                .sort((a, b) => b.amount - a.amount)
                .map((item) => {
                  const name = 'categoryName' in item
                    ? translateCategoryName(item.categoryId, item.categoryName, item.categoryIsDefault)
                    : translateCategoryName(item.subcategoryId, item.subcategoryName);
                  const isCategory = drillDown.level === 'categories';

                  return (
                    <button
                      key={isCategory ? (item as CategoryBreakdown).categoryId : (item as SubcategoryBreakdown).subcategoryId}
                      onClick={() => isCategory && handleCategoryClick(item as CategoryBreakdown)}
                      disabled={!isCategory}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg border border-gray-100 transition-colors text-left ${
                        isCategory
                          ? 'hover:bg-gray-50 hover:border-gray-200 cursor-pointer'
                          : 'cursor-default'
                      }`}
                    >
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: item.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {item.percentage.toFixed(0)}%
                      </span>
                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {formatCurrency(item.amount)}
                      </span>
                      {isCategory && (
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (noCard) return mainContent;
  return <div className="card h-full">{mainContent}</div>;
}
